import { apiClient } from '@/lib/api-client';
import type {
  ListUsersInput,
  UsersListResponse,
  UserDetail,
  ProvisionUserInput,
  ProvisionResult,
  ResendInviteResult,
  CreatePlatformProfileInput,
  ProvisionScopedUserInput,
  ProvisionScopedUserResult,
  EffectivePerk,
  GrantedPerk,
  ApplyPerkGrantBatchInput,
  PerkGrantBatchResult,
  UpdateUserInput,
  IdentityEntityType,
  PerkEventsResponse,
  IdentityEventsResponse,
  IdentityCorrelatedEntityEventsResponse,
  IdentityOperationalAlertsResponse,
  IdentityOperationalAlert,
  ListIdentityOperationalAlertsInput,
  OperationalAlertFacet,
  IdentityOperationalAlertStatus,
  CloseIdentityOperationalAlertInput,
  UserDuplicateDocument,
} from '../../domain/entities/user.entity';

export class UsersApiDatasource {
  async list(input?: ListUsersInput): Promise<UsersListResponse> {
    const { data } = await apiClient.get<UsersListResponse>(
      '/admin/identity/users',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          ...(input?.email ? { email: input.email } : {}),
        },
      },
    );
    return data;
  }

  async getById(userId: string): Promise<UserDetail> {
    const { data } = await apiClient.get<UserDetail>(
      `/admin/identity/users/${userId}`,
    );
    return data;
  }

  async provision(input: ProvisionUserInput): Promise<ProvisionResult> {
    // `POST /admin/identity/accounts` é idempotente por e-mail: `created` diz se
    // a conta é NOVA ou JÁ existia (no-op). O `user` vem sempre — conta sem
    // pessoa deixou de existir quando `Account.userId` virou obrigatório.
    //
    // Com `scopeId`, provisionamos em SILÊNCIO e damos o acesso em seguida: o
    // convite sai da criação do perfil, então mandar os dois seria e-mail em
    // dobro com o segundo invalidando o link do primeiro. Se o segundo passo
    // falhar, a pessoa fica com conta e sem convite — o erro sobe para o modal,
    // e dar o acesso pela página dela reenvia.
    const grantsAccess = Boolean(input.scopeId);
    const { data } = await apiClient.post<{
      id: string;
      user: { id: string };
      created: boolean;
    }>('/admin/identity/accounts', {
      email: input.email,
      user: { name: input.name },
      ...(grantsAccess ? { sendFirstLoginEmail: false } : {}),
    });

    if (!input.scopeId) return { created: data.created, accessGranted: false };

    await this.createPlatformProfile({
      userId: data.user.id,
      name: input.name,
      scopeId: input.scopeId,
    });
    return { created: data.created, accessGranted: true };
  }

  // Reenvia o convite de primeiro acesso (link para definir a senha). `sent: false`
  // quando a pessoa já definiu a senha (conta verificada) — resposta distinguível.
  async resendInvite(accountId: string): Promise<ResendInviteResult> {
    const { data } = await apiClient.post<ResendInviteResult>(
      `/admin/identity/accounts/${accountId}/first-login`,
      {},
    );
    return data;
  }

  async createPlatformProfile(
    input: CreatePlatformProfileInput,
  ): Promise<void> {
    await apiClient.post('/admin/identity/platform-profiles', input);
  }

  // Rota única do iamEditor: o back confere a autoridade dele no scope antes de
  // gravar qualquer coisa. `user` só vai quando há nome — no detalhe a conta já
  // existe, e o back não altera a pessoa.
  async provisionScopedUser(
    input: ProvisionScopedUserInput,
  ): Promise<ProvisionScopedUserResult> {
    const { data } = await apiClient.post<ProvisionScopedUserResult>(
      '/admin/identity/scoped-users',
      {
        email: input.email,
        scopeId: input.scopeId,
        ...(input.name ? { user: { name: input.name } } : {}),
      },
    );
    return data;
  }

  async unlinkIdentity(accountId: string, method: string): Promise<void> {
    await apiClient.delete(
      `/admin/identity/accounts/${accountId}/identities/${encodeURIComponent(
        method,
      )}`,
    );
  }

  async activateProfile(profileId: string): Promise<void> {
    await apiClient.patch(
      `/admin/identity/platform-profiles/${profileId}/activate`,
    );
  }

  async deactivateProfile(profileId: string): Promise<void> {
    await apiClient.patch(
      `/admin/identity/platform-profiles/${profileId}/deactivate`,
    );
  }

  async listGrantedPerks(profileId: string): Promise<GrantedPerk[]> {
    const { data } = await apiClient.get<GrantedPerk[]>(
      `/admin/perks/profile/${profileId}`,
    );
    return data;
  }

  /**
   * O conjunto efetivo, com a linhagem dos derivados. O `scopeId` é o do perfil:
   * sem ele o back não filtra derivado de outra plataforma.
   */
  async listEffectivePerks(
    profileId: string,
    scopeId: string,
  ): Promise<EffectivePerk[]> {
    const { data } = await apiClient.get<EffectivePerk[]>(
      `/admin/perks/profile/${profileId}/effective`,
      { params: { scopeId } },
    );
    // `derivedFrom` nasceu depois do `derived`: um back anterior a ele manda o
    // derivado sem o campo, e o editor inteiro cairia num `for...of` sobre
    // `undefined`. Sem linhagem, os avisos só não aparecem.
    return data.map((perk) => ({
      ...perk,
      derivedFrom: perk.derivedFrom ?? [],
    }));
  }

  /**
   * O diff inteiro do perfil, em uma escrita só. O admin é o concedente, então é
   * ele que informa os valores — `grantPolicyParams` vazio só passa em perk
   * `permanent`. `presetId` é só auditoria: registra de qual preset a linha
   * saiu, sem criar vínculo. Cada `revoke` derruba **um** pacote.
   */
  async applyPerkGrantBatch(
    input: ApplyPerkGrantBatchInput,
  ): Promise<PerkGrantBatchResult> {
    const { data } = await apiClient.post<PerkGrantBatchResult>(
      '/admin/perks/batch',
      input,
    );
    return data;
  }

  async updateUser(userId: string, data: UpdateUserInput): Promise<void> {
    await apiClient.patch(`/admin/identity/users/${userId}`, data);
  }

  async resetPassword(accountId: string): Promise<void> {
    await apiClient.post(
      `/admin/identity/accounts/${accountId}/password-credential`,
    );
  }

  async setTemporaryPassword(
    accountId: string,
    password: string,
  ): Promise<void> {
    await apiClient.post(
      `/admin/identity/accounts/${accountId}/temporary-password`,
      { password },
    );
  }

  async resendVerification(accountId: string): Promise<void> {
    await apiClient.post('/auth/send-verification', { accountId });
  }

  async getEntityEvents(
    entityType: IdentityEntityType,
    entityId: string,
    params: { page: number; perPage: number },
  ): Promise<IdentityEventsResponse> {
    const { data } = await apiClient.get<IdentityEventsResponse>(
      `/admin/identity/entities/${entityType}/${entityId}/events`,
      { params },
    );
    return data;
  }

  async getEntityEventsByCorrelationId(
    correlationId: string,
    params: { page: number; perPage: number },
  ): Promise<IdentityCorrelatedEntityEventsResponse> {
    const { data } =
      await apiClient.get<IdentityCorrelatedEntityEventsResponse>(
        '/admin/identity/entity-events',
        { params: { correlationId, ...params } },
      );
    return data;
  }

  // --- Alertas operacionais (fase B5 do plano do outbox transacional) ---

  async listOperationalAlertFacets(
    status?: IdentityOperationalAlertStatus,
  ): Promise<OperationalAlertFacet[]> {
    const { data } = await apiClient.get<OperationalAlertFacet[]>(
      '/admin/identity/operational-alerts/facets',
      { params: { status } },
    );
    return data;
  }

  async getDuplicateDocument(userId: string): Promise<UserDuplicateDocument> {
    const { data } = await apiClient.get<UserDuplicateDocument>(
      `/admin/identity/users/${userId}/duplicate-document`,
    );
    return data;
  }

  async listOperationalAlerts(
    input?: ListIdentityOperationalAlertsInput,
  ): Promise<IdentityOperationalAlertsResponse> {
    const { data } = await apiClient.get<IdentityOperationalAlertsResponse>(
      '/admin/identity/operational-alerts',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          status: input?.status || undefined,
          type: input?.type || undefined,
          entityType: input?.entityType || undefined,
          entityId: input?.entityId || undefined,
          order: input?.order,
        },
      },
    );
    return data;
  }

  async resolveOperationalAlert(
    id: string,
    input: CloseIdentityOperationalAlertInput,
  ): Promise<IdentityOperationalAlert> {
    const { data } = await apiClient.post<IdentityOperationalAlert>(
      `/admin/identity/operational-alerts/${id}/resolve`,
      input,
    );
    return data;
  }

  async dismissOperationalAlert(
    id: string,
    input: CloseIdentityOperationalAlertInput,
  ): Promise<IdentityOperationalAlert> {
    const { data } = await apiClient.post<IdentityOperationalAlert>(
      `/admin/identity/operational-alerts/${id}/dismiss`,
      input,
    );
    return data;
  }

  /**
   * Histórico de perk do perfil. Rota do Delivery porque a coleção é dele — o
   * feed de eventos do usuário costura as duas no cliente.
   */
  async getProfilePerkEvents(
    profileId: string,
    params: { page: number; perPage: number },
  ): Promise<PerkEventsResponse> {
    const { data } = await apiClient.get<PerkEventsResponse>(
      `/admin/perks/profile/${profileId}/events`,
      { params },
    );
    return data;
  }
}
